import { cn } from '../ui/utils';
import { LucideIcon } from 'lucide-react';
import { Card } from '../ui/card';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import { memo } from 'react';

interface StatCardProps {
  title: string;
  value: string | number;
  icon?: LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  description?: string;
  className?: string;
  valueClassName?: string;
  /**
   * Full value to show in tooltip (when value is compacted)
   */
  fullValue?: string;
  /**
   * Test ID for testing purposes
   * Defaults to kebab-case version of title
   */
  testId?: string;
}

/**
 * StatCard - Displays a KPI/metric card with title, value, optional trend and icon
 * 
 * Memoized for performance. Used in dashboards to show key metrics.
 * Automatically adjusts font size for large values to prevent overflow.
 * Shows tooltip with full value when value is compacted.
 * 
 * @example
 * ```tsx
 * <StatCard
 *   title="Receita Total"
 *   value="R$ 1,5 mi"
 *   fullValue="R$ 1.523.456,78"
 *   icon={DollarSign}
 *   trend={{ value: 12.5, isPositive: true }}
 *   description="vs. mês anterior"
 * />
 * ```
 */
export const StatCard = memo(function StatCard({
  title,
  value,
  icon: Icon,
  trend,
  description,
  className,
  valueClassName,
  fullValue,
  testId,
}: StatCardProps) {
  // Generate testId from title if not provided
  const generatedTestId = testId || `stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`;
  
  // Determine text size based on value length to prevent overflow
  const valueStr = typeof value === 'string' ? value : String(value);
  const valueLength = valueStr.length;
  
  // Adjust font size for long values
  const getFontSizeClass = () => {
    if (valueLength > 20) return 'text-xl'; // Very long values (e.g., R$ 1.234.567,89)
    if (valueLength > 15) return 'text-2xl'; // Long values (e.g., R$ 123.456,78)
    return 'text-3xl'; // Normal values (default)
  };
  
  // Check if value is compacted (contains mil, mi, bi from Portuguese compact notation)
  const isCompact = valueStr.includes('mil') || valueStr.includes('mi') || valueStr.includes('bi');
  
  const valueElement = (
    <p 
      className={cn(
        'mt-2 truncate transition-all',
        getFontSizeClass(),
        valueClassName,
        isCompact && 'cursor-help'
      )}
      data-testid={`${generatedTestId}-value`}
      aria-label={`${title}: ${fullValue || value}`}
    >
      {value}
    </p>
  );

  return (
    <Card 
      className={cn('p-4 md:p-6', className)}
      data-testid={generatedTestId}
    >
      <div className="flex items-center justify-between gap-2 md:gap-3">
        <div className="flex-1 min-w-0">
          <p 
            className="text-sm text-muted-foreground"
            data-testid={`${generatedTestId}-title`}
          >
            {title}
          </p>
          
          {isCompact && fullValue ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {valueElement}
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={8}>
                <small>{fullValue}</small>
              </TooltipContent>
            </Tooltip>
          ) : (
            valueElement
          )}
          
          {trend && (
            <p
              className={cn(
                'mt-2 text-sm',
                trend.isPositive ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'
              )}
              data-testid={`${generatedTestId}-trend`}
              aria-label={`Tendência: ${trend.isPositive ? 'positiva' : 'negativa'} ${Math.abs(trend.value).toFixed(1)}%`}
            >
              {trend.value > 0 ? '↑' : '↓'} {Math.abs(trend.value).toFixed(1)}%
            </p>
          )}
          {description && (
            <small 
              className="mt-1 text-muted-foreground block"
              data-testid={`${generatedTestId}-description`}
            >
              {description}
            </small>
          )}
        </div>
        {Icon && (
          <div className="rounded-full bg-primary/10 p-2 md:p-3 flex-shrink-0" aria-hidden="true">
            <Icon className="h-5 w-5 md:h-6 md:w-6 text-primary" />
          </div>
        )}
      </div>
    </Card>
  );
});
